<template>
    <div
        :id="id"
        :class="{ filled: filled, focused: focused, disabled: disabled, error, 'no-label': !label }"
        class="tl-input-text"
    >
        <div
            v-if="title"
            class="tl-title"
        >{{ title }}</div>
        <div class="tl-input-wrapper">
            <input
                ref="input"
                :value="modelState"
                :type="options?.type || 'text'"
                :disabled="disabled"
                :maxlength="maxlength"
                @input="handleInput($event.target.value)"
                @focus="focused = true"
                @blur="focused = false"
                @keyup="handleKey"
                @keypress="handleKeyPress"
            />
            <div
                v-if="label"
                class="tl-label"
            >
                <div v-html="label" />
            </div>
        </div>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import useTools from 'widget-components/utilities/tools'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import { isNotNil } from 'ramda'

const { wait } = useTools()

defineOptions({ inheritAttrs: false })

const props = defineProps({
    id: String,
    modelValue: [String, null],
    disabled: Boolean,
    focus: Boolean,
    maxlength: [Number, String],
    error: {
        type: [String, Boolean],
        default: undefined,
    },
    options: Object,
    initialValue: String,
    label: String,
    title: String,
    validator: String,
    blockPath: Array,
})

const UPPERCASE_VALIDATORS = ['ID_CARD_SERIES', 'ID_CARD_SERIES_AND_NUMBER']

const uppercase = computed(() => UPPERCASE_VALIDATORS.includes(props.validator))

const emit = defineEmits(['update:modelValue', 'submit', 'mounted'])

const input = ref(null)

const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)

const filled = computed(() => !!modelState.value)
const focused = ref(false)
const label = computed(() => props.label)

const handleInput = (val) => {
    modelState.value = uppercase.value
        ? val?.toUpperCase() || val
        : val
}

onMounted(async () => {
    if (props.focus) {
        await wait(800)
        input.value?.focus()
    }

    if (isNotNil(props.initialValue)) {
        modelState.value = props.initialValue
    }

    emit('mounted', input.value)
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
})

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})

const handleKeyPress = (event) => {
    if (props.validator && props.validator === 'ID_CARD_SERIES_AND_NUMBER') {
        if (!/^[a-zA-Z0-9_]*$/.test(event.key)) {
            event.preventDefault()
        }
    }
}

const handleKey = (event) => {
    const code = event.keyCode

    // ENTER
    if (code === 13 || event.key === 'enter') {
        emit('submit')
    }
}
</script>

<style scoped lang="sass">
@import './InputText.sass'
</style>
